import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export const EASE = [0.16, 1, 0.3, 1];

export function MaskedLines({ lines, className = "", lineClassName = "", delay = 0, as = "h1" }) {
  const Tag = as;
  return (
    <Tag className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
          <motion.span
            className={`block ${lineClassName}`}
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay: delay + i * 0.13, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

export function Reveal({ children, delay = 0, y = 36, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function CountUp({ to, suffix = "", className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    let raf;
    const start = performance.now();
    const dur = 1500;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return (
    <span ref={ref} className={className}>
      {val}
      {suffix}
    </span>
  );
}

export function Eyebrow({ children, className = "" }) {
  return (
    <p className={`text-[11px] font-semibold uppercase tracking-[0.28em] text-gold ${className}`}>
      {children}
    </p>
  );
}

export function ArrowLink({ to, children, light = false, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`group inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
        light ? "text-white hover:text-gold" : "text-ink hover:text-gold"
      }`}
    >
      {children}
      <ArrowRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  );
}

export function GoldButton({ to, children, testId, type, onClick }) {
  const cls =
    "inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-[11px] font-bold uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:bg-white hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-ink";
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <button type={type || "button"} onClick={onClick} data-testid={testId} className={cls}>
      {children}
    </button>
  );
}

export function OutlineButton({ to, children, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-8 py-4 text-[11px] font-bold uppercase tracking-[0.22em] text-white transition-all duration-300 hover:border-white hover:bg-white hover:text-ink hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
    >
      {children}
    </Link>
  );
}
